import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV15Params {
  /**
   * 文本提示词，必填参数
   * @minLength 1
   * @maxLength 2500
   */
  prompt: string;
  /**
   * 视频时长(秒)，可选参数。支持字符串"5"、"10"或整数5、10
   * @default 5
   * @values 5 | 10 | "5" | "10"
   */
  duration?: 5 | 10 | '5' | '10';
  /**
   * 视频宽高比，可选参数
   * @default "16:9"
   * @values "16:9" | "9:16" | "1:1"
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 负面提示词，可选参数。用于指定不希望出现在生成视频中的内容
   * @minLength 1
   * @maxLength 2500
   */
  negative_prompt?: string;
  /**
   * CFG比例参数，可选参数。用于控制生成内容与提示词的匹配程度，数值越高，模型的灵活性程度越低，与用户提示的关联性也就越强
   * @default 0.5
   * @min 0
   * @max 1
   */
  cfg_scale?: number;
  /**
   * 相机控制参数，可选参数。用于控制视频中的镜头运动和角度
   */
  camera_control?: Record<string, any>;
  /**
   * 外部任务ID，可选参数。用于关联外部系统的任务
   */
  external_task_id?: string;
}

export interface KlingV15Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v1_5
 *
 * API Documentation: https://app.klingai.com/global/dev/document-api/apiReference/model/textToVideo
 * 
 * Kling AI V1.5 视频生成实现。支持从文本提示词生成视频。
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV15 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV15({
 *   // 参数见 KlingV15Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV15Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingV15(
  params: KlingV15Params,
): Promise<KlingV15Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v1_5',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV15Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
